<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {router} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import {Assessment, CapacityFramework, Form, Participant, TeamRole} from "@/types";
import {useDate} from "@/composables/date";
import {useDisplay} from "vuetify/framework";
import {useNumber} from "@/composables/number";
import {computed} from "vue";
import IconTooltip from "@/components/tooltips/IconTooltip.vue";
import AssessmentActionsBtn from "@/Pages/assessments/components/AssessmentActionsBtn.vue";
import VersionChip from "@/components/chips/VersionChip.vue";
import SendEmailBtn from "@/components/buttons/SendEmailBtn.vue";

defineOptions({layout: BaseLayout})

const {mobile} = useDisplay()
const {shortDate} = useDate()
const {percentage} = useNumber()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    assessment: Assessment
    form: Form
    participants: Array<Participant>
    isMember: boolean
    isMemberOfCapacityFramework: boolean
    role: TeamRole
}

const props = defineProps<Props>();

const isReadonly = computed(() => {
    return props.assessment.status === 'completed' || props.assessment.status === 'closed';
})

const editAssessment = () => {
    router.get(route('assessments.edit', { assessment: props.assessment.id }))
}

const closeAssessment = () => {
    router.put(route('assessments.close', {
        assessment: props.assessment.id
    }))
}

const openAssessment = () => {
    router.put(route('assessments.open', {
        assessment: props.assessment.id
    }))
}

const toggleAssessmentAsStarred = () => {
    router.put(route('assessments.star', {
        assessment: props.assessment.id,
    }))
}

const deleteAssessment = () => {
    router.delete(route('assessments.destroy', {
        assessment: props.assessment.id
    }))
}

const goToCapacityFramework = () => {
    router.get(route('capacity-frameworks.show', { capacityFramework: props.capacityFramework.id, version: props.capacityFramework.version_id }))
}
</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between">
            <v-col
                cols="9"
                class="py-0"
            >
                <h1 class="text-h4 font-weight-bold">
                    {{ props.title }}
                </h1>
            </v-col>
            <v-col
                cols="3"
                align-self="start"
                class="py-0"
            >
                <div class="text-end">
                    <v-btn
                        v-if="!isReadonly && props.role !== 'guest'"
                        color="black"
                        variant="text"
                        border="sm"
                        :class="mobile ? 'mt-3' : ''"
                        :block="mobile"
                        @click="editAssessment"
                    >
                        <span class="font-weight-regular">
                            Edit
                        </span>
                    </v-btn>
                    <v-btn
                        v-if="isReadonly && isMember"
                        :class="mobile ? 'mt-3' : ''"
                        :block="mobile"
                        :href="route('capacity-frameworks.progression.index', {capacityFramework: props.capacityFramework.id, team_id: props.assessment.team_id})"
                        target="_blank"
                    >
                        <span class="font-weight-regular">
                            View Progress
                        </span>
                    </v-btn>
                    <assessment-actions-btn
                        :assessment="props.assessment"
                        :hidden="!props.isMember || props.role === 'guest'"
                        :hide-admin-actions="!props.isMemberOfCapacityFramework"
                        :readonly="isReadonly"
                        @star="toggleAssessmentAsStarred"
                        @close="closeAssessment"
                        @open="openAssessment"
                        @delete="deleteAssessment"
                    />
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col
                cols="12"
                md="8"
                class="py-1 text-body-2"
            >
                <slot />
            </v-col>
            <v-col
                cols="12"
                md="4"
                class="sidebar"
            >
                <v-list class="pa-0">
                    <v-list-item>
                        <v-list-item-title class="text-body-2 text-black font-weight-bold">
                            Framework
                            <version-chip>
                                {{ props.capacityFramework.version_id }}
                            </version-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle
                            class="d-inline-flex custom-link text-primary"
                            @click.stop="goToCapacityFramework"
                        >
                            {{ props.capacityFramework.name }}
                        </v-list-item-subtitle>
                        <v-divider class="mt-3 mb-1" />
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title class="text-body-2 text-black font-weight-bold">
                            Visibility
                        </v-list-item-title>
                        <v-list-item-subtitle
                            class="d-inline-flex"
                        >
                            {{ props.assessment.is_public ? 'Public' : 'Private' }}
                            <icon-tooltip
                                :icon="props.assessment.is_public ? 'mdi-web' : 'mdi-lock-outline'"
                                class="ml-1"
                                size="16"
                            >
                                {{ props.assessment.is_public ? 'Public - The assessment can be viewed by anyone.' : 'Private - The assessment can only be viewed by invited resource members.' }}
                            </icon-tooltip>
                        </v-list-item-subtitle>
                        <v-divider class="mt-3 mb-1" />
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title class="text-body-2 text-black font-weight-bold">
                            Main contact
                            <send-email-btn :email="props.assessment.user.email" />
                        </v-list-item-title>
                        <v-list-item-subtitle
                            class="d-inline-flex"
                        >
                            {{ props.assessment.user.name }}
                        </v-list-item-subtitle>
                        <v-divider class="mt-3 mb-1" />
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title class="text-body-2 text-black font-weight-bold">
                            Resource
                            <send-email-btn :email="props.assessment.team.email" />
                        </v-list-item-title>
                        <v-list-item-subtitle
                            class="d-inline-flex"
                        >
                            {{ props.assessment.team.name }}
                        </v-list-item-subtitle>

                        <v-divider class="mt-3 mb-1" />
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title class="text-body-2 text-black font-weight-bold mb-2">
                            Dates
                        </v-list-item-title>
                        <div
                            class="mb-2 text-body-2"
                        >
                            Start: <span class="ml-1 text-medium-emphasis">{{ props.assessment.start_date ? shortDate(props.assessment.start_date) : 'None' }}</span>
                        </div>
                        <div
                            class="mb-2 text-body-2"
                        >
                            Due: <span class="ml-3 text-medium-emphasis">{{ props.assessment.due_date ? shortDate(props.assessment.due_date) : 'None' }}</span>
                        </div>
                        <v-divider class="mt-3 mb-1" />
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title class="text-body-2 text-black font-weight-bold mb-2">
                            {{ participants.length }} {{ participants.length === 1 ? 'Participant' : 'Participants' }}
                        </v-list-item-title>
                        <v-tooltip
                            v-for="participant in props.participants"
                            :key="participant.email"
                            location="top"
                            content-class="bg-black border-sm text-tooltip"
                            max-width="200"
                        >
                            <template #activator="{ props: bind }">
                                <v-avatar
                                    v-bind="bind"
                                    :image="`https://ui-avatars.com/api/?name=${participant.name}`"
                                    size="28"
                                    class="mr-1"
                                />
                            </template>
                            {{ participant.name }} <span v-if="participant.internal_role"> ({{ participant.internal_role }})</span>
                        </v-tooltip>
                        <v-divider class="mt-3 mb-1" />
                    </v-list-item>
                    <v-list-item>
                        <v-list-item-title class="text-body-2 text-black font-weight-bold mb-2">
                            Progress
                        </v-list-item-title>
                        <div
                            v-if="props.capacityFramework.capacity_indicators_count > 0"
                            class="d-flex flex-row align-center w-100"
                        >
                            <div class="text-grey-darken-3 text-sm mr-2 text-no-wrap">
                                {{ props.assessment.answers_count }}/{{ props.capacityFramework.capacity_indicators_count }} complete
                            </div>
                            <v-progress-linear
                                :model-value="(props.assessment.answers_count / props.capacityFramework.capacity_indicators_count) * 100"
                                color="primary"
                                class="flex-grow-1"
                                height="6"
                                rounded
                                rounded-bar
                            />
                            <div
                                class="text-grey-darken-3 text-sm ml-2"
                            >
                                {{ percentage(props.assessment.answers_count/props.capacityFramework.capacity_indicators_count) }}
                            </div>
                        </div>

                        <v-divider class="mt-3 mb-1" />
                    </v-list-item>
                </v-list>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
.sidebar {
    flex: 0 0 300px;
    max-width: 300px;
    position: sticky;
    top: 64px;
    height: fit-content;
}

:deep(small) {
    font-size: 0.75rem;
}

.text-sm {
    font-size: 0.875rem;
}
</style>
